// src\themes\lino\elements\Product\ProductGallery.tsx
"use client"

import { useEffect, useState } from "react"
import { Swiper, SwiperSlide } from "swiper/react"
import { Navigation, Keyboard, Thumbs } from "swiper/modules"
import { ChevronLeft, ChevronRight, X, Maximize2 } from "lucide-react"
import Image from "next/image"
import { Button } from "@/components/ui/button"
import type { Swiper as SwiperType } from "swiper"

import "swiper/css"
import "swiper/css/navigation"
import CustomButton from "../../components/CustomButton"
import ProductHoverMedia from "../../components/ProductHoverMedia"

type ImageType = {
  url: string
  alt?: string
  description?: string
}

type ProductGalleryProps = {
  images: ImageType[]
  product: {
    title?: string
    discount_percent: number
    discount_title: string
    thumbnail?: ImageType
    thumbnail_hover?: ImageType | null
  }
}

export default function ProductGallery({ images, product }: ProductGalleryProps) {
  const [isGalleryOpen, setIsGalleryOpen] = useState(false)
  const [currentIndex, setCurrentIndex] = useState(0)
  const [thumbsSwiper, setThumbsSwiper] = useState<SwiperType | null>(null)
  const [modalSwiper, setModalSwiper] = useState<SwiperType | null>(null)

  // 🔍 دیباگ موقت — بعد از پیدا کردن مشکل حذفش کن
  console.log('🔍 product.thumbnail_hoverrrrrrrrrrrrrrrrrrrrrrrr:', product?.thumbnail_hover);
  console.log('🔍 full product objectyttttttttttttttttt:', product);

  const displayImages: ImageType[] = product?.thumbnail?.url
    ? [
      {
        url: product.thumbnail.url,
        alt: product.title || "تصویر اصلی محصول",
      },
      ...images,
    ]
    : images

  useEffect(() => {
    document.body.style.overflow = isGalleryOpen ? "hidden" : ""
    return () => {
      document.body.style.overflow = ""
    }
  }, [isGalleryOpen])

  if (displayImages.length === 0) return null

  const toggleFullscreen = () => {
    if (!document.fullscreenElement) {
      document.documentElement.requestFullscreen()
    } else {
      document.exitFullscreen()
    }
  }

  return (
    <div className="w-full" dir="rtl">
      <div className="relative overflow-hidden bg-primary-100">
        <Swiper
          modules={[Navigation, Keyboard, Thumbs]}
          thumbs={{ swiper: thumbsSwiper && !thumbsSwiper.destroyed ? thumbsSwiper : null }}
          navigation={{ prevEl: ".main-prev", nextEl: ".main-next" }}
          keyboard={{ enabled: true }}
          slidesPerView={1}
          spaceBetween={0}
          loop={displayImages.length > 1}
          speed={400}
          onSlideChange={(swiper) => setCurrentIndex(swiper.realIndex)}
          className="w-full aspect-square"
        >
          {displayImages.map((img, index) => (
            <SwiperSlide key={index}>
              <div
                className="relative w-full h-full cursor-pointer group"
                onClick={() => {
                  setCurrentIndex(index)
                  setIsGalleryOpen(true)
                }}
              >
                <Image
                  src={img.url || "/placeholder.svg"}
                  alt={img.description || img.alt || product.title || "تصویر محصول"}
                  fill
                  className="object-cover"
                  sizes="(max-width: 768px) 100vw, 600px"
                  priority={index === 0}
                />

                {/* روی اولین اسلاید (که همون تصویر اصلی محصوله)، با
                هاور، عکس/ویدئوی thumbnail_hover نشون داده می‌شه */}
                {index === 0 && product.thumbnail_hover?.url && (
                  <ProductHoverMedia
                    url={product.thumbnail_hover.url}
                    alt={product.title || "تصویر محصول"}
                    sizes="(max-width: 768px) 100vw, 600px"
                  />
                )}

                {product.discount_percent > 0 && (
                  <span className="absolute top-0 ltr:left-0 rtl:right-0 bg-primary-900 text-primary-0 text-[10px] uppercase tracking-[0.14em] px-3 py-1.5 z-10">
                    {product.discount_percent}% {product.discount_title}
                  </span>
                )}
              </div>
            </SwiperSlide>
          ))}
        </Swiper>

        {displayImages.length > 1 && (
          <>
            <CustomButton
              size="icon"
              variant="secondary"
              className="main-prev absolute right-3 top-1/2 -translate-y-1/2 z-20 !bg-primary-0/95 border border-primary-200"
            >
              <ChevronRight className="w-4 h-4" />
            </CustomButton>
            <CustomButton
              size="icon"
              variant="secondary"
              className="main-next absolute left-3 top-1/2 -translate-y-1/2 z-20 !bg-primary-0/95 border border-primary-200"
            >
              <ChevronLeft className="w-4 h-4" />
            </CustomButton>
          </>
        )}

        {displayImages.length > 1 && (
          <div className="absolute bottom-4 left-4 bg-primary-950/70 text-primary-0 px-3 py-1 text-[11px] tracking-wide z-10">
            {currentIndex + 1} / {displayImages.length}
          </div>
        )}
      </div>

      {displayImages.length > 1 && (
        <div className="relative mt-3">
          <Swiper
            modules={[Navigation, Thumbs]}
            watchSlidesProgress
            slidesPerView={5}
            spaceBetween={10}
            freeMode
            loop={false}
            speed={400}
            navigation={{ prevEl: ".thumb-prev", nextEl: ".thumb-next" }}
            breakpoints={{
              640: { slidesPerView: 5, spaceBetween: 10 },
              1024: { slidesPerView: 6, spaceBetween: 12 },
            }}
            onSwiper={setThumbsSwiper}
            className="w-full"
          >
            {displayImages.map((img, index) => (
              <SwiperSlide key={index}>
                <div className="relative aspect-square overflow-hidden cursor-pointer border border-transparent opacity-70 hover:opacity-100 transition-all duration-300 [&.swiper-slide-thumb-active]:opacity-100 [&.swiper-slide-thumb-active]:border-primary-900">
                  <Image
                    src={img.url}
                    alt={img.alt || "تصویر"}
                    fill
                    className="object-cover"
                    sizes="(max-width: 768px) 25vw, 120px"
                  />
                </div>
              </SwiperSlide>
            ))}
          </Swiper>
        </div>
      )}

      {isGalleryOpen && (
        <div className="fixed inset-0 z-50 bg-primary-50/97 animate-in fade-in duration-300">
          <div className="absolute top-0 left-0 right-0 z-50 flex items-center justify-between p-4">
            <span className="text-primary-900 text-sm">
              {currentIndex + 1} / {displayImages.length}
            </span>

            <div className="flex items-center gap-1">
              <Button
                size="icon"
                variant="ghost"
                onClick={toggleFullscreen}
                className="text-primary-900 hover:bg-primary-100 h-10 w-10"
              >
                <Maximize2 className="w-4 h-4" />
              </Button>
              <Button
                size="icon"
                variant="ghost"
                onClick={() => setIsGalleryOpen(false)}
                className="text-primary-900 hover:bg-primary-100 h-10 w-10"
              >
                <X className="w-5 h-5" />
              </Button>
            </div>
          </div>

          {displayImages.length > 1 && (
            <>
              <Button
                size="icon"
                variant="secondary"
                className="modal-prev rounded-full absolute right-4 top-1/2 -translate-y-1/2 z-50 h-11 w-11 border border-primary-200"
              >
                <ChevronRight className="w-5 h-5" />
              </Button>
              <Button
                size="icon"
                variant="secondary"
                className="modal-next rounded-full absolute left-4 top-1/2 -translate-y-1/2 z-50 h-11 w-11 border border-primary-200"
              >
                <ChevronLeft className="w-5 h-5" />
              </Button>
            </>
          )}

          <Swiper
            modules={[Navigation, Keyboard]}
            navigation={{ prevEl: ".modal-prev", nextEl: ".modal-next" }}
            keyboard={{ enabled: true }}
            initialSlide={currentIndex}
            loop={displayImages.length > 1}
            speed={400}
            onSwiper={setModalSwiper}
            onSlideChange={(swiper) => setCurrentIndex(swiper.realIndex)}
            className="w-full h-full"
          >
            {displayImages.map((img, i) => (
              <SwiperSlide key={i} className="flex! items-center justify-center!">
                <Image
                  src={img.url}
                  alt={img.description || img.alt || "تصویر محصول"}
                  width={1920}
                  height={1080}
                  className="max-w-full h-svh max-h-[calc(100vh-8rem)] w-auto object-contain"
                  priority={i === currentIndex}
                />
              </SwiperSlide>
            ))}
          </Swiper>

          {displayImages.length > 1 && (
            <div className="absolute bottom-0 left-0 right-0 z-50 p-4">
              <div className="flex gap-2 justify-center max-w-4xl mx-auto overflow-x-auto p-2 scrollbar-hide">
                {displayImages.map((img, index) => (
                  <button
                    key={index}
                    onClick={() => modalSwiper?.slideTo(index)}
                    className={`relative shrink-0 w-14 h-14 overflow-hidden border transition-all duration-300 ${currentIndex === index
                      ? "border-primary-900 opacity-100"
                      : "border-transparent opacity-50 hover:opacity-100"
                      }`}
                  >
                    <Image
                      src={img.url || "/placeholder.svg"}
                      alt={`Thumbnail ${index + 1}`}
                      fill
                      className="object-cover"
                      sizes="56px"
                    />
                  </button>
                ))}
              </div>
            </div>
          )}
        </div>
      )}
    </div>
  )
}